import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Heading,
  Text,
  Button,
  VStack,
  HStack,
  Badge,
  Input,
  FormControl,
  FormLabel,
  Select,
  Spinner,
  Alert,
  AlertIcon,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  IconButton,
  Flex,
  Spacer,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Divider,
} from "@chakra-ui/react";
import { FiArrowLeft, FiEye, FiChevronDown } from "react-icons/fi";
import { useUser, useFindMany, useFindOne } from "@gadgetinc/react";
import { api } from "../api";
import { hasAnyRole } from "../lib/roleUtils";

const STATUS_COLORS = {
  pending: "yellow",
  in_progress: "blue",
  completed: "green",
  expired: "red",
};

const SIM_SESSION_STATUS_COLORS = {
  active: "blue",
  paused: "orange",
  completed: "green",
  abandoned: "gray",
};

function formatDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatDateTime(d) {
  if (!d) return "—";
  return new Date(d).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

// ── Transcript Modal ──
function TranscriptModal({ isOpen, onClose, sessionId }) {
  const [{ data: simSession, fetching }] = useFindOne(api.simulationSession, sessionId, {
    select: {
      id: true,
      status: true,
      startedAt: true,
      completedAt: true,
      elapsedTimeMinutes: true,
      conversationTranscript: true,
      currentState: true,
      evaluationNotes: {
        markdown: true,
      },
      scoreBreakdown: true,
      totalScore: true,
    },
    pause: !sessionId,
  });

  const transcript = simSession?.conversationTranscript || [];
  const evalNotes = simSession?.evaluationNotes;
  const score = simSession?.scoreBreakdown;
  const simulatorQaReview = score?.simulatorQaReview;
  const simulatorQaReport = simulatorQaReview?.report;
  const eventLog = simSession?.currentState?.eventLog || [];

  // Parse evaluationNotes markdown if it's a richText field
  let evalMarkdown = null;
  if (evalNotes) {
    evalMarkdown = typeof evalNotes === "object" ? evalNotes.markdown || JSON.stringify(evalNotes) : evalNotes;
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent bg="#1e293b" color="#f8fafc" maxW="700px">
        <ModalHeader borderBottom="1px solid" borderColor="whiteAlpha.100">
          Session Details
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          {fetching ? (
            <Flex justify="center" py={8}><Spinner size="lg" color="blue.400" /></Flex>
          ) : !simSession ? (
            <Text color="gray.400">Session not found.</Text>
          ) : (
            <VStack align="stretch" spacing={5}>
              {/* Meta */}
              <HStack spacing={4} wrap="wrap">
                <Badge colorScheme={SIM_SESSION_STATUS_COLORS[simSession.status] || "gray"}>{simSession.status}</Badge>
                <Text fontSize="sm" color="gray.400">Started: {formatDateTime(simSession.startedAt)}</Text>
                {simSession.completedAt && <Text fontSize="sm" color="gray.400">Completed: {formatDateTime(simSession.completedAt)}</Text>}
                {simSession.elapsedTimeMinutes != null && <Text fontSize="sm" color="gray.400">{simSession.elapsedTimeMinutes} min</Text>}
                {simSession.totalScore != null && <Badge colorScheme="purple">Score: {simSession.totalScore}</Badge>}
              </HStack>

              <Flex align="stretch" gap={4} direction={{ base: "column", lg: "row" }}>
                <Box flex="1" minW={0}>
                  <Text fontWeight="600" mb={2} color="blue.300">Conversation Transcript</Text>
                  {transcript.length > 0 ? (
                    <VStack align="stretch" spacing={2} maxH="420px" overflowY="auto" p={3} bg="whiteAlpha.50" borderRadius="md">
                      {transcript.map((turn, i) => {
                        const isUser = turn.role === "user" || turn.speaker === "student";
                        return (
                          <Box
                            key={i}
                            alignSelf={isUser ? "flex-end" : "flex-start"}
                            bg={isUser ? "blue.700" : "whiteAlpha.100"}
                            px={3}
                            py={2}
                            borderRadius="lg"
                            maxW="95%"
                          >
                            <Text fontSize="xs" color="gray.400" mb={0.5}>
                              {turn.speaker || turn.role || (isUser ? "Student" : "System")}
                            </Text>
                            <Text fontSize="sm" whiteSpace="pre-wrap">
                              {turn.text || turn.content || turn.message || turn.user || turn.assistant || JSON.stringify(turn)}
                            </Text>
                          </Box>
                        );
                      })}
                    </VStack>
                  ) : (
                    <Box p={3} bg="whiteAlpha.50" borderRadius="md">
                      <Text fontSize="sm" color="gray.400">No transcript available.</Text>
                    </Box>
                  )}
                </Box>

                <Box flex="1" minW={0}>
                  <Text fontWeight="600" mb={2} color="orange.300">Simulator QA Report</Text>
                  {simulatorQaReport ? (
                    <Box p={3} bg="whiteAlpha.50" borderRadius="md" maxH="420px" overflowY="auto">
                      {simulatorQaReview?.generatedAt && (
                        <Text fontSize="xs" color="gray.400" mb={2}>
                          Generated: {formatDateTime(simulatorQaReview.generatedAt)} | Model: {simulatorQaReview.model || "unknown"}
                        </Text>
                      )}
                      <Text fontSize="sm" mb={2}>{simulatorQaReport.summary || "No summary provided."}</Text>
                      {typeof simulatorQaReport.qualityScore !== "undefined" && (
                        <Badge colorScheme="purple" mb={3}>Quality Score: {simulatorQaReport.qualityScore}</Badge>
                      )}
                      {Array.isArray(simulatorQaReport.issues) && simulatorQaReport.issues.length > 0 && (
                        <VStack align="stretch" spacing={2}>
                          {simulatorQaReport.issues.map((issue, idx) => (
                            <Box key={idx} p={2} borderRadius="md" bg="whiteAlpha.100">
                              <HStack spacing={2} mb={1}>
                                <Badge
                                  colorScheme={
                                    issue.severity === "critical" || issue.severity === "high" ? "red" :
                                      issue.severity === "medium" ? "yellow" : "gray"
                                  }
                                  fontSize="2xs"
                                >
                                  {issue.severity || "issue"}
                                </Badge>
                                <Badge colorScheme="blue" fontSize="2xs">{issue.category || "general"}</Badge>
                              </HStack>
                              <Text fontSize="sm" fontWeight="600">{issue.title || "Untitled issue"}</Text>
                              {issue.evidence && <Text fontSize="xs" color="gray.300" mt={1}>Evidence: {issue.evidence}</Text>}
                              {issue.recommendedFix && <Text fontSize="xs" color="green.300" mt={1}>Fix: {issue.recommendedFix}</Text>}
                            </Box>
                          ))}
                        </VStack>
                      )}
                      {!simulatorQaReport.issues && (
                        <Text fontSize="sm" whiteSpace="pre-wrap">{JSON.stringify(simulatorQaReport, null, 2)}</Text>
                      )}
                    </Box>
                  ) : (
                    <Box p={3} bg="whiteAlpha.50" borderRadius="md">
                      <Text fontSize="sm" color="gray.400">No simulator QA report saved yet.</Text>
                    </Box>
                  )}
                </Box>
              </Flex>

              {/* Debrief / Evaluation */}
              {evalMarkdown && (
                <Box>
                  <Text fontWeight="600" mb={2} color="green.300">Evaluation Notes</Text>
                  <Box bg="whiteAlpha.50" p={3} borderRadius="md" fontSize="sm" whiteSpace="pre-wrap">
                    {evalMarkdown}
                  </Box>
                </Box>
              )}

              {score && (
                <Box>
                  <Text fontWeight="600" mb={2} color="purple.300">Score Breakdown</Text>
                  <Box bg="whiteAlpha.50" p={3} borderRadius="md" fontSize="sm">
                    {score.narrative && <Text mb={2} whiteSpace="pre-wrap">{score.narrative}</Text>}
                    {score.competencies && Array.isArray(score.competencies) && (
                      <VStack align="stretch" spacing={1} mt={2}>
                        {score.competencies.map((c, i) => (
                          <HStack key={i} justify="space-between">
                            <Text>{c.name || c.label}</Text>
                            <Badge colorScheme={c.score >= 3 ? "green" : c.score >= 2 ? "yellow" : "red"}>{c.score ?? "—"}</Badge>
                          </HStack>
                        ))}
                      </VStack>
                    )}
                    {score.interventionChecklist && Array.isArray(score.interventionChecklist) && (
                      <VStack align="stretch" spacing={1} mt={3}>
                        <Text fontWeight="500" fontSize="xs" color="gray.400" textTransform="uppercase">Interventions</Text>
                        {score.interventionChecklist.map((item, i) => (
                          <HStack key={i}>
                            <Text fontSize="sm">{item.performed ? "✓" : "✗"}</Text>
                            <Text fontSize="sm" color={item.performed ? "green.300" : "red.300"}>{item.name || item.label}</Text>
                          </HStack>
                        ))}
                      </VStack>
                    )}
                    {!score.narrative && !score.competencies && !score.interventionChecklist && (
                      <Text whiteSpace="pre-wrap">{JSON.stringify(score, null, 2)}</Text>
                    )}
                  </Box>
                </Box>
              )}

              <Accordion allowToggle>
                <AccordionItem border="1px solid" borderColor="whiteAlpha.200" borderRadius="md">
                  <AccordionButton>
                    <Box flex="1" textAlign="left" fontWeight="600" color="yellow.300">
                      Raw Event Logs ({Array.isArray(eventLog) ? eventLog.length : 0})
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                  <AccordionPanel pt={2}>
                    <Box bg="blackAlpha.400" borderRadius="md" p={3} maxH="320px" overflowY="auto">
                      <Text fontFamily="mono" fontSize="xs" whiteSpace="pre-wrap" color="gray.200">
                        {Array.isArray(eventLog) && eventLog.length > 0
                          ? JSON.stringify(eventLog, null, 2)
                          : "No event log entries"}
                      </Text>
                    </Box>
                  </AccordionPanel>
                </AccordionItem>
              </Accordion>
            </VStack>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}

// ── Main Page ──
export default function AdminSimSessions() {
  const navigate = useNavigate();
  const user = useUser();

  // Filters
  const [statusFilter, setStatusFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  // Detail modal
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedSessionId, setSelectedSessionId] = useState(null);

  const isAdmin = user && hasAnyRole(user.roles, ["admin", "system-admin"]);
  const isSystemAdmin = user && hasAnyRole(user.roles, ["system-admin"]);
  const institution = user?.institution || "";

  // Build filter
  const filter = useMemo(() => {
    const f = {};
    // Legacy-safe: system admins can view all records, while institution admins
    // only apply institution filter when institution is present on their account.
    if (!isSystemAdmin && institution) {
      f.sharedBy = { institution: { equals: institution } };
    }
    if (statusFilter) f.status = { equals: statusFilter };
    if (dateFrom) f.createdAt = { ...(f.createdAt || {}), greaterThanOrEqual: new Date(dateFrom).toISOString() };
    if (dateTo) f.createdAt = { ...(f.createdAt || {}), lessThanOrEqual: new Date(dateTo + "T23:59:59").toISOString() };
    return f;
  }, [institution, isSystemAdmin, statusFilter, dateFrom, dateTo]);

  const [{ data: sessions, fetching, error }] = useFindMany(api.voiceSimSession, {
    filter,
    sort: { createdAt: "Descending" },
    first: 100,
    select: {
      id: true,
      status: true,
      studentEmail: true,
      attemptsAllowed: true,
      attemptsUsed: true,
      expiresAt: true,
      createdAt: true,
      metadata: true,
      scenarioId: true,
      student: { id: true, firstName: true, lastName: true, email: true },
      sharedBy: { id: true, firstName: true, lastName: true },
      simulationSessions: {
        edges: {
          node: {
            id: true,
            status: true,
            startedAt: true,
            completedAt: true,
            elapsedTimeMinutes: true,
            totalScore: true,
            evaluationNotes: {
              markdown: true,
            },
            scoreBreakdown: true,
          },
        },
      },
    },
    pause: !institution,
  });

  if (!isAdmin) {
    return (
      <Container maxW="container.lg" py={8}>
        <Alert status="error" bg="red.900" borderRadius="md">
          <AlertIcon />
          Admin access required for this page.
        </Alert>
      </Container>
    );
  }

  const openDetail = (simSessionId) => {
    setSelectedSessionId(simSessionId);
    onOpen();
  };

  return (
    <Box minH="100vh" bg="#0f172a" color="#f8fafc">
      <Container maxW="container.xl" py={6}>
        {/* Header */}
        <Flex align="center" mb={6}>
          <IconButton
            icon={<FiArrowLeft />}
            aria-label="Back to admin"
            variant="ghost"
            color="gray.400"
            mr={3}
            onClick={() => navigate("/admin")}
          />
          <Heading size="lg">Voice Sim Sessions</Heading>
        </Flex>

        {/* Filters */}
        <Flex gap={4} mb={6} wrap="wrap">
          <FormControl maxW="200px">
            <FormLabel fontSize="sm" color="gray.400">Status</FormLabel>
            <Select
              size="sm"
              bg="#1e293b"
              borderColor="whiteAlpha.200"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">All</option>
              <option value="pending">Pending</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
              <option value="expired">Expired</option>
            </Select>
          </FormControl>
          <FormControl maxW="180px">
            <FormLabel fontSize="sm" color="gray.400">From</FormLabel>
            <Input size="sm" type="date" bg="#1e293b" borderColor="whiteAlpha.200" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
          </FormControl>
          <FormControl maxW="180px">
            <FormLabel fontSize="sm" color="gray.400">To</FormLabel>
            <Input size="sm" type="date" bg="#1e293b" borderColor="whiteAlpha.200" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
          </FormControl>
        </Flex>

        {/* Content */}
        {fetching ? (
          <Flex justify="center" py={12}><Spinner size="xl" color="blue.400" /></Flex>
        ) : error ? (
          <Alert status="error" bg="red.900" borderRadius="md">
            <AlertIcon />
            {error.message}
          </Alert>
        ) : !sessions || sessions.length === 0 ? (
          <Box textAlign="center" py={12}>
            <Text color="gray.500" fontSize="lg">No voice sim sessions found.</Text>
          </Box>
        ) : (
          <Accordion allowMultiple>
            {sessions.map((sess) => {
              const studentName = sess.student
                ? `${sess.student.firstName || ""} ${sess.student.lastName || ""}`.trim() || sess.student.email
                : (sess.metadata?.recipientDisplayName || sess.studentEmail);
              const sharedByName = sess.sharedBy
                ? `${sess.sharedBy.firstName || ""} ${sess.sharedBy.lastName || ""}`.trim()
                : "—";
              const simSessions = sess.simulationSessions?.edges?.map((e) => e.node) || [];

              return (
                <AccordionItem key={sess.id} border="none" mb={2}>
                  <AccordionButton
                    bg="#1e293b"
                    borderRadius="lg"
                    _hover={{ bg: "#334155" }}
                    px={4}
                    py={3}
                  >
                    <Flex flex="1" align="center" gap={3} wrap="wrap">
                      <Badge colorScheme={STATUS_COLORS[sess.status] || "gray"} fontSize="xs">
                        {sess.status}
                      </Badge>
                      <Text fontWeight="600" fontSize="sm" noOfLines={1} maxW="250px">
                        {sess.metadata?.scenarioTitle || (sess.scenarioId ? `Scenario #${sess.scenarioId}` : "Untitled Scenario")}
                      </Text>
                      <Text fontSize="sm" color="gray.400">{studentName}</Text>
                      <Spacer />
                      <Text fontSize="xs" color="gray.500">by {sharedByName}</Text>
                      <Text fontSize="xs" color="gray.500">
                        {sess.attemptsUsed}/{sess.attemptsAllowed} attempts
                      </Text>
                      <Text fontSize="xs" color="gray.500">{formatDate(sess.createdAt)}</Text>
                      {sess.expiresAt && (
                        <Text fontSize="xs" color={new Date(sess.expiresAt) < new Date() ? "red.400" : "gray.500"}>
                          Exp: {formatDate(sess.expiresAt)}
                        </Text>
                      )}
                    </Flex>
                    <AccordionIcon ml={2} />
                  </AccordionButton>
                  <AccordionPanel bg="#1e293b" borderRadius="0 0 lg lg" px={4} pb={4}>
                    {simSessions.length === 0 ? (
                      <Text fontSize="sm" color="gray.500" py={3}>No simulation attempts yet.</Text>
                    ) : (
                      <Table size="sm" variant="unstyled">
                        <Thead>
                          <Tr>
                            <Th color="gray.500" fontSize="xs">#</Th>
                            <Th color="gray.500" fontSize="xs">Status</Th>
                            <Th color="gray.500" fontSize="xs">Started</Th>
                            <Th color="gray.500" fontSize="xs">Completed</Th>
                            <Th color="gray.500" fontSize="xs">Duration</Th>
                            <Th color="gray.500" fontSize="xs">Score</Th>
                            <Th color="gray.500" fontSize="xs">Eval</Th>
                            <Th color="gray.500" fontSize="xs"></Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {simSessions.map((sim, idx) => (
                            <Tr key={sim.id} _hover={{ bg: "whiteAlpha.50" }}>
                              <Td fontSize="sm">{idx + 1}</Td>
                              <Td>
                                <Badge colorScheme={SIM_SESSION_STATUS_COLORS[sim.status] || "gray"} fontSize="xs">
                                  {sim.status}
                                </Badge>
                              </Td>
                              <Td fontSize="sm" color="gray.300">{formatDateTime(sim.startedAt)}</Td>
                              <Td fontSize="sm" color="gray.300">{formatDateTime(sim.completedAt)}</Td>
                              <Td fontSize="sm" color="gray.300">
                                {sim.elapsedTimeMinutes != null ? `${sim.elapsedTimeMinutes} min` : "—"}
                              </Td>
                              <Td fontSize="sm">{sim.totalScore != null ? sim.totalScore : "—"}</Td>
                              <Td>
                                <HStack spacing={1}>
                                  {sim.evaluationNotes && <Badge colorScheme="green" fontSize="2xs">Notes</Badge>}
                                  {sim.scoreBreakdown && <Badge colorScheme="purple" fontSize="2xs">Score</Badge>}
                                </HStack>
                              </Td>
                              <Td>
                                <IconButton
                                  icon={<FiEye />}
                                  aria-label="View details"
                                  size="xs"
                                  variant="ghost"
                                  color="blue.300"
                                  onClick={() => openDetail(sim.id)}
                                />
                              </Td>
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
                    )}
                  </AccordionPanel>
                </AccordionItem>
              );
            })}
          </Accordion>
        )}
      </Container>

      {/* Detail Modal */}
      <TranscriptModal isOpen={isOpen} onClose={onClose} sessionId={selectedSessionId} />
    </Box>
  );
}
